import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useHistory } from 'react-router-dom'
import { App, Modal, Button, Row, Col, Tooltip, Typography } from 'antd'
import { LoadingOutlined, PlusCircleOutlined, MailOutlined, DownloadOutlined } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'
import Api from '@vacationtracker/shared/services/api'
import { getGoogleScopes, getInvitations, getNumberOfActiveUsers, getLeaveTypesList, getLocationActiveUsersInfo } from '../../../graphql/custom-queries'
import * as logger from '../../../services/logger'
import { StatusBadge } from '../../../design-system/StatusBadge'
import { ConfirmModal } from '../../../design-system/ConfirmModal'

import { googleAuth } from './authInstances'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { useFeatureFlagEnabled as posthogFeatureFlagEnabled } from 'posthog-js/react'
import { useShouldEnableFeatures } from '../../../store/use-should-enable-features'
import { notificationStore } from '../../../context/notificationsContext/store'

import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'

import IntlMessages from '../../../util/IntlMessages'
import { PageHeader } from '../../../design-system/PageHeader'
import CircularProgress from '../../../components/circular-progress'
import { Search } from '../../../components/search'
import { InviteAndManageUsersWithEmail } from '../../../components/invite-and-manage-users-with-email'

import { IUserFilteredInfo } from '../../../types/users'
import { IGetGoogleScopes, IGetInvitations, IGetLeaveTypeList, IGetLocationActiveUsersInfo } from '../../../types/custom-queries'
import { IGetLeaveTypesList } from '../../../types/leave-types'
import { EmailInviteStatusEnum, IEmailInvite, UserRoleEnum } from '@vacationtracker/shared/types/user'
import { buildInviteStatusByEmail, getEmailInviteStatus } from '@vacationtracker/shared/functions/email-invite-status'
import { resolveFeaturePermissions } from '@vacationtracker/shared/functions/resolve-feature-permissions'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { IUserInvitedEventParams, UsersPageViewTab } from './types'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { GOOGLE_READ_USER_PERMISSION } from '../../../constants'
import { clearWizardState, isWizardDismissed } from '../../../util/onboarding-wizard-storage'
import { wait } from '@vacationtracker/shared/functions/wait'
import { shouldSuggestLocationDelete } from './should-suggest-location-delete'

import { useIsPostHogFeatureFlagEnabledForCompany } from '@vacationtracker/shared/hooks/use-is-postHog-featureFlag-enabled-for-company'
import { track } from '../../../services/analytics/analytics'
import { showApiErrorNotification } from './showApiErrorNotification'
import { useUsersInfinite } from './hooks/useUsersInfinite'
import { useUsersFilters } from './hooks/useUsersFilters'
import { useColumnPrefs } from './hooks/useColumnPrefs'
import { usePermissions } from './hooks/usePermissions'
import { useLocationFilterScope } from '../../../hooks/use-location-filter-scope'
import { useUsersReferenceData } from './hooks/useUsersReferenceData'
import { useStoredPreference } from './hooks/useStoredPreference'
import { UsersTableSection } from './components/UsersTableSection'
import { ExportUsersModal } from './components/ExportUsersModal'
import { UsersFilterBar } from './components/UsersFilterBar'
import { COLUMN_IDS, PROFILE_DEFAULT_ORDER, PROFILE_HIDDEN_BY_DEFAULT, columnLabelId, leaveTypeColumnId } from './columns/columnIds'
import { buildExportColumns } from './columns/exportColumns'
import { buildDepartmentOptions } from './columns/departmentOptions'
import VotitoSurveyConnected from '../../../components/votito-survey/VotitoSurveyConnected'
import { SyncUsersControls } from './components/SyncUsersControls'
import { ColumnSettingsPopover } from './components/ColumnSettingsPopover'

const USERS_TABLE_SURVEY_ID = 'f9467ad9-0724-4bc4-af69-52bdf097c278'

const UsersPage = (): React.ReactElement => {
  const location = useLocation()
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const {
    authUser: { id: authUserId, platform, email, locale },
  } = useAppSelector(selectAuthUserSlice)
  const { actionNotifications, addActionNotifications, eventsNotifications } = useContext(notificationStore)
  const permissions = usePermissions()
  const { amIAdmin, isFullAdmin, isLocationAdmin, isMicrosoftBillingPayment } = permissions
  const { scopeLocations } = useLocationFilterScope()

  if (!authCompany?.platform) {
    throw new Error('Company is required!')
  }

  const saveFilters = new URLSearchParams(location.search).get('saveFilters') !== 'false'

  const usersFilters = useUsersFilters({
    userId: authUserId,
    pathname: location.pathname,
    search: location.search,
    saveFilters,
  })
  const { filters } = usersFilters

  const [viewTab, setViewTab] = useState<UsersPageViewTab>('profile')
  const reference = useUsersReferenceData({
    // Only plain approvers are narrowed to their approver departments. A location admin (also one who
    // approves) sees users of every department in their locations, so the filter must list them all.
    approverScoped: permissions.isApprover && !amIAdmin,
    authUserId,
  })
  const { teams, locations, labels, isLoading } = reference
  const [totalActiveUsersCount, setTotalActiveUsersCount] = useState<number | undefined>(undefined)
  const [filteredActiveUsersCount, setFilteredActiveUsersCount] = useState<number | undefined>(undefined)

  const [showInviteUsersModal, setShowInviteUsersModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [reinvitingEmail, setReinvitingEmail] = useState<string | null>(null)
  const [invites, setInvites] = useState<Partial<IEmailInvite>[]>([])
  const [invitationLocale, setInvitationLocale] = useState<LocaleEnum>(locale || LocaleEnum.en)
  const [notInvitedUsers, setNotInvitedUsers] = useState<Partial<IEmailInvite>[]>([])
  const [pendingOrExpiredInvitedUsers, setPendingOrExpiredInvitedUsers] = useState<IEmailInvite[]>([])
  const [isSubmittingInviteRequest, setIsSubmittingInviteRequest] = useState(false)
  const [inviteStatusFilter, setInviteStatusFilter] = useState<EmailInviteStatusEnum[]>([])
  const [searchResetKey, setSearchResetKey] = useState(0)

  const [leaveTypes, setLeaveTypes] = useState<IGetLeaveTypeList[]>([])
  const [leaveTypesLoaded, setLeaveTypesLoaded] = useState(false)
  const [leaveTabActivated, setLeaveTabActivated] = useState(false)
  const [leaveDisplayUnit, setLeaveDisplayUnit] = useStoredPreference<'days' | 'hours'>(`${authUserId}#usersTableLeaveUnit`, (stored) =>
    stored === 'days' || stored === 'hours' ? stored : authCompany?.hourlyLeaveAccounting ? 'hours' : 'days'
  )
  const [density, setDensity] = useStoredPreference<'comfortable' | 'compact'>(`${authUserId}#usersTableDensity`, (stored) =>
    stored === 'comfortable' ? 'comfortable' : 'compact'
  )
  const [showBalance, setShowBalance] = useStoredPreference<boolean>(`${authUserId}#usersTableBalances`, (stored) => stored !== 'false')
  const [confirmState, setConfirmState] = useState<{
    title: string
    message: string
    okLabel: string
    danger?: boolean
    onOk: () => void
  } | null>(null)

  const [getInvitationsQuery] = useManualQuery<IGetInvitations>(getInvitations)
  const [getLeaveTypesListQuery] = useManualQuery<IGetLeaveTypesList>(getLeaveTypesList)
  const [getGoogleScopesQuery] = useManualQuery<IGetGoogleScopes, { accessToken: string }>(getGoogleScopes)
  const [getNumberOfActiveUsersQuery] = useManualQuery<{ getCompany: { numberOfActiveUsers: number } }>(getNumberOfActiveUsers)
  const [getLocationActiveUsersInfoQuery] = useManualQuery<IGetLocationActiveUsersInfo, { locationId: string; locationIds: string[] }>(getLocationActiveUsersInfo)

  const importEmailUsersEnabled = posthogFeatureFlagEnabled(FeatureFlagEnum.emailUserImport)
  const shouldEnableFeatures = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)
  const shouldDisableFeatureFlagSyncUsers = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.disableSyncUsers, true)

  const { enabled: teamEnergyEnabled } = useIsPostHogFeatureFlagEnabledForCompany(FeatureFlagEnum.teamEnergy)
  const energyEnabled = Boolean(teamEnergyEnabled) && (amIAdmin || permissions.isApprover)

  const inviteFilterEnabled = platform === 'email' && amIAdmin
  const effectiveFilters = useMemo(
    () => (inviteFilterEnabled && inviteStatusFilter.length > 0 ? { ...filters, inviteStatuses: inviteStatusFilter } : filters),
    [filters, inviteFilterEnabled, inviteStatusFilter]
  )
  const usersQuery = useUsersInfinite(effectiveFilters, energyEnabled)
  const { users, scannedCount } = usersQuery

  useEffect(() => {
    getLeaveTypesListQuery()
      .then((response) => {
        if (response.data) {
          setLeaveTypes(response.data.getLeaveTypeList)
        }
      })
      .catch((error) => logger.warning(error))
      .finally(() => setLeaveTypesLoaded(true))
  }, [])

  useEffect(() => {
    if (viewTab === 'leaveDetails' && !leaveTabActivated) {
      setLeaveTabActivated(true)
    }
  }, [viewTab, leaveTabActivated])

  const columnPrefs = useColumnPrefs({
    userId: authUserId,
    tab: 'profile',
    defaultOrder: PROFILE_DEFAULT_ORDER,
    hiddenByDefault: PROFILE_HIDDEN_BY_DEFAULT,
  })

  const activeLeaveTypes = useMemo(
    () => leaveTypes.filter((leaveType) => leaveType.isActive && !leaveType.deleted).sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [leaveTypes]
  )

  const leaveDetailsDefaultOrder = useMemo(
    () => [COLUMN_IDS.team, ...activeLeaveTypes.flatMap((leaveType) => [leaveTypeColumnId(leaveType.id, 'quota'), leaveTypeColumnId(leaveType.id, 'balance')])],
    [activeLeaveTypes]
  )

  const leaveColumnPrefs = useColumnPrefs({
    userId: authUserId,
    tab: 'leaveDetails',
    defaultOrder: leaveDetailsDefaultOrder,
    hiddenByDefault: [],
  })

  const exportColumns = useMemo(
    () =>
      buildExportColumns({
        profileColumns: columnPrefs.visibleColumnOrder,
        leaveColumns: leaveColumnPrefs.visibleColumnOrder,
      }),
    [columnPrefs.visibleColumnOrder, leaveColumnPrefs.visibleColumnOrder]
  )

  const shouldHighlightAddUsers = amIAdmin && !isWizardDismissed(authUserId, authCompany?.id) && (totalActiveUsersCount === undefined || totalActiveUsersCount < 2)

  useEffect(() => {
    if (platform === 'email' && amIAdmin) {
      fetchInvitations().catch((error) => logger.warning({ errorType: 'ERROR FETCH INVITATIONS', error }))
    }
  }, [eventsNotifications, amIAdmin])

  useEffect(() => {
    void syncGoogleScopes()
  }, [])

  useEffect(() => {
    if (location.search.includes('openImportUsers=true')) {
      importNewUsers()
    }
  }, [location.search])

  useEffect(() => {
    void fetchUserCount()
  }, [eventsNotifications])

  // The table owns its own cache invalidation; the counts shown in the header are this page's, and
  // they refresh on the same signal - a correlationId leaving the list once its projection is done.
  const previousActionNotifications = useRef<string[]>(actionNotifications)
  useEffect(() => {
    const previous = previousActionNotifications.current
    previousActionNotifications.current = actionNotifications
    if (previous.some((correlationId) => !actionNotifications.includes(correlationId))) {
      void fetchUserCount()
    }
  }, [actionNotifications])

  useEffect(() => {
    const haveFilters = usersFilters.hasActiveDimensionFilters || inviteStatusFilter.length > 0
    if (scannedCount === undefined) {
      return
    }
    if (isLocationAdmin && filters.status === 'ACTIVE') {
      if (!haveFilters) {
        setTotalActiveUsersCount(scannedCount)
      }
      setFilteredActiveUsersCount(scannedCount)
    } else if (filters.status === 'ACTIVE') {
      setFilteredActiveUsersCount(haveFilters ? scannedCount : undefined)
    }
  }, [scannedCount, filters.status, usersFilters.hasActiveDimensionFilters, inviteStatusFilter, isLocationAdmin])

  const fetchInvitations = async () => {
    const response = await getInvitationsQuery()
    if (!response.data || response.error) throw response.error

    const notInvited: IEmailInvite[] = []
    const pendingOrExpired: IEmailInvite[] = []

    for (const invitation of response.data.getInvitations) {
      if (invitation.status === 'not-invited' || invitation.status === 'expired') {
        notInvited.push(invitation)
      }
      if (invitation.status === 'pending' || invitation.status === 'expired') {
        pendingOrExpired.push(invitation)
      }
    }

    setNotInvitedUsers(notInvited)
    setPendingOrExpiredInvitedUsers(pendingOrExpired)
  }

  const removeInvite = (data) => {
    setInvites((previous) => previous.filter((invite) => invite.email !== data.email))
  }

  const inviteStatusByEmail = useMemo(() => buildInviteStatusByEmail(pendingOrExpiredInvitedUsers), [pendingOrExpiredInvitedUsers])

  const getUserInviteStatus = useCallback((user: IUserFilteredInfo): EmailInviteStatusEnum => getEmailInviteStatus(user, inviteStatusByEmail), [inviteStatusByEmail])

  const changeLocale = (newLocale: LocaleEnum) => {
    setInvitationLocale(newLocale)
  }

  // Google's own users-read scope can be granted outside Vacation Tracker; record it when it appears
  // so the sync controls know they are allowed to run.
  const syncGoogleScopes = async () => {
    if (authCompany.platform !== 'google') {
      return
    }
    try {
      const googleScopesResponse = await getGoogleScopesQuery({ variables: { accessToken: googleAuth.getAccessToken() } })
      if (!googleScopesResponse.data || googleScopesResponse.error) throw googleScopesResponse.error
      const googleApiScopes = googleScopesResponse.data.getGoogleTokenInfo.scopes as string[]
      const vtGooogleIntegrationScopes = googleScopesResponse.data.getGoogleIntegrationSettings?.scopes
      if (googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION) && !vtGooogleIntegrationScopes?.includes(GOOGLE_READ_USER_PERMISSION)) {
        const userInfo = googleAuth.getSignedInUser()
        await Api.post('/core/event', {
          eventType: 'GOOGLE_INTEGRATION',
          eventGroup: 'INTEGRATION',
          scope: googleApiScopes.join(' '),
          domainName: userInfo.hd,
        })
      }
    } catch (error) {
      logger.warning(error)
    }
  }

  const confirmInviteUser = (user: IUserFilteredInfo) => {
    const isReinvite = getUserInviteStatus(user) === EmailInviteStatusEnum.expired
    setConfirmState({
      title: formatMessage({ id: 'app.invite' }),
      message: formatMessage({ id: 'users.inviteUserConfirm' }, { email: user.email }),
      okLabel: formatMessage({ id: isReinvite ? 'users.reinvite' : 'app.invite' }),
      onOk: () => (isReinvite ? reinviteSingle(user.email) : sendInvite(user.email)),
    })
  }

  const fetchUserCount = async () => {
    if (isLocationAdmin) {
      return
    }
    try {
      const response = await getNumberOfActiveUsersQuery()
      if (!response.data || response.error) throw response.error
      setTotalActiveUsersCount(response.data.getCompany.numberOfActiveUsers as number)
      setFilteredActiveUsersCount(response.data.getCompany.numberOfActiveUsers as number)
    } catch (error) {
      logger.warning({
        errorType: 'ERROR FETCH USER COUNT',
        error,
      })
      setTotalActiveUsersCount(0)
      setFilteredActiveUsersCount(0)
    }
  }

  const importNewUsers = () => {
    if (isMicrosoftBillingPayment) {
      return history.push('/app/users/manage-licences')
    } else if (platform === 'email') {
      return history.push('/app/users/import-email-users')
    } else {
      return history.push('/app/users/import')
    }
  }

  const adjustActiveCounts = (status: string, count: number) => {
    if (status === 'INACTIVE' || status === 'DELETED') {
      setTotalActiveUsersCount((prev) => (prev !== undefined ? prev - count : prev))
      setFilteredActiveUsersCount((prev) => (prev !== undefined ? Math.max(prev - count, 0) : prev))
    } else if (status === 'ACTIVE') {
      setTotalActiveUsersCount((prev) => (prev !== undefined ? prev + count : prev))
      setFilteredActiveUsersCount((prev) => (prev !== undefined ? prev + count : prev))
    }
  }

  const fetchLocationDeleteSuggestion = async (params: { locationId?: string; affectedUserWasActive: boolean }): Promise<{ locationId: string; locationName: string } | null> => {
    // Deleting a location requires the Admin role, so the suggestion is limited to full admins
    if (!isFullAdmin || !params.locationId) {
      return null
    }
    try {
      const response = await getLocationActiveUsersInfoQuery({
        variables: { locationId: params.locationId, locationIds: [params.locationId] },
      })
      const locationInfo = response.data?.getLocation
      const totalUsers = response.data?.getUsersFiltered?.totalUsers
      if (!locationInfo || typeof totalUsers !== 'number') {
        return null
      }
      const shouldSuggest = shouldSuggestLocationDelete({
        activeCount: totalUsers,
        isDefault: Boolean(locationInfo.isDefault),
        affectedUserWasActive: params.affectedUserWasActive,
      })
      return shouldSuggest ? { locationId: locationInfo.id, locationName: locationInfo.name } : null
    } catch (error) {
      logger.warning({ errorType: 'ERROR LOCATION DELETE SUGGESTION', error })
      return null
    }
  }

  const suggestLocationDelete = (params: { locationId: string; locationName: string }) => {
    setConfirmState({
      title: formatMessage({ id: 'users.suggestDeleteLocationTitle' }, { locationName: params.locationName }),
      message: formatMessage({ id: 'users.suggestDeleteLocationContent' }, { locationName: params.locationName }),
      okLabel: formatMessage({ id: 'locations.deleteLocation' }),
      danger: true,
      onOk: async () => {
        // Let the preceding user event settle before deleting the location; same race guard as the holiday delete on LocationPage
        await wait(2000)
        try {
          const response = await Api.post('/core/event', {
            eventType: 'LOCATION_DELETED',
            eventGroup: 'LOCATION',
            locationId: params.locationId,
          })
          notification.open({
            key: response.correlationId,
            message: formatMessage({ id: 'location.deleteInProgress' }, { locationName: params.locationName }),
            icon: <LoadingOutlined />,
            duration: 0,
          })
          addActionNotifications([response.correlationId])
        } catch (error) {
          logger.warning({ errorType: 'ERROR DELETE LOCATION', error })
          showApiErrorNotification(notification, formatMessage, error)
        }
      },
    })
  }

  const postInvites = async (emails: string[], reinvite?: boolean) => {
    const body: Partial<IUserInvitedEventParams> = {
      eventType: 'USERS_INVITED',
      eventGroup: 'USER',
      userId: authUserId,
      emails,
      locale: invitationLocale,
      ...(reinvite ? { reinvite } : {}),
    }
    const response = await Api.post('/core/event', body)
    addActionNotifications([response.correlationId])
  }

  const sendInvites = async () => {
    try {
      setIsSubmittingInviteRequest(true)
      await postInvites(invites.map((invite) => invite.email).filter(Boolean) as string[])
      clearWizardState(authUserId, authCompany.id)
      setShowInviteUsersModal(false)
    } catch (error) {
      logger.error(error)
    } finally {
      setIsSubmittingInviteRequest(false)
    }
  }

  const reinviteSingle = async (inviteEmail: string) => {
    try {
      setReinvitingEmail(inviteEmail)
      await postInvites([inviteEmail], true)
    } catch (error) {
      logger.error(error)
    } finally {
      setReinvitingEmail(null)
    }
  }

  const sendInvite = async (inviteEmail: string) => {
    try {
      await postInvites([inviteEmail])
      clearWizardState(authUserId, authCompany.id)
    } catch (error) {
      logger.error(error)
    }
  }

  const deleteInvite = async (inviteEmail: string) => {
    try {
      const response = await Api.post('/core/event', {
        eventType: 'USER_INVITATION_DELETED',
        eventGroup: 'USER',
        email: inviteEmail,
        userId: authUserId,
      })
      addActionNotifications([response.correlationId])
    } catch (error) {
      logger.error(error)
    }
  }

  const showConfirmDeleteInvite = (inviteEmail: string) => {
    setConfirmState({
      title: formatMessage({ id: 'users.deleteInvite' }),
      message: formatMessage({ id: 'users.deleteInviteConfirm' }, { email: inviteEmail }),
      okLabel: formatMessage({ id: 'users.deleteInvite' }),
      danger: true,
      onOk: () => deleteInvite(inviteEmail),
    })
  }

  const onSearch = (name: string) => {
    usersFilters.setName(name)
  }

  const changeStatusFilter = (status: string) => {
    usersFilters.setStatus(status)
  }

  const getCTAButtonTranslation = () => {
    if (isMicrosoftBillingPayment) {
      return 'app.microsoft.manageLicenses'
    } else if (platform === 'email' && importEmailUsersEnabled) {
      return 'users.createUsers'
    } else if (platform !== 'email') {
      return 'users.importNewUsers'
    } else {
      return 'users.inviteUsers'
    }
  }

  const pendingInvites = pendingOrExpiredInvitedUsers.filter((invite) => invite.status === 'pending')

  const departmentFilterOptions = useMemo(() => buildDepartmentOptions(teams), [teams])

  const locationFilterOptions = useMemo(() => scopeLocations(locations).map((loc) => ({ id: loc.id, name: loc.name, countryCode: loc.countryCode })), [locations, scopeLocations])
  const labelFilterOptions = useMemo(() => labels.map((label) => ({ id: label.id, name: label.name })), [labels])

  const columnSettingsOptions = useMemo(
    () =>
      PROFILE_DEFAULT_ORDER.filter((id) => id !== COLUMN_IDS.labels || shouldEnableFeatures).map((id) => ({
        id,
        label: <IntlMessages id={columnLabelId(id)} />,
      })),
    [shouldEnableFeatures]
  )

  const leaveColumnSettingsOptions = useMemo(
    () => [
      { id: COLUMN_IDS.team, label: <IntlMessages id="app.department" /> },
      ...activeLeaveTypes.flatMap((leaveType) => [
        {
          id: leaveTypeColumnId(leaveType.id, 'quota'),
          label: (
            <>
              {leaveType.name} - <IntlMessages id="users.table.quota" />
            </>
          ),
        },
        {
          id: leaveTypeColumnId(leaveType.id, 'balance'),
          label: (
            <>
              {leaveType.name} - <IntlMessages id="app.used" />
            </>
          ),
        },
      ]),
    ],
    [activeLeaveTypes]
  )

  const statusOptions = [
    { id: 'ACTIVE', name: formatMessage({ id: 'app.active' }) },
    ...(amIAdmin
      ? [
          { id: 'INACTIVE', name: formatMessage({ id: 'app.inactive' }) },
          { id: 'DELETED', name: formatMessage({ id: 'users.deletedUsers' }) },
        ]
      : []),
  ]

  const inviteStatusOptions = [
    { id: EmailInviteStatusEnum.notInvited, name: formatMessage({ id: 'users.uninvited' }) },
    { id: EmailInviteStatusEnum.pending, name: formatMessage({ id: 'users.invitedPending' }) },
    { id: EmailInviteStatusEnum.expired, name: formatMessage({ id: 'users.invitedExpired' }) },
    { id: EmailInviteStatusEnum.accepted, name: formatMessage({ id: 'users.table.inviteAccepted' }) },
  ]

  const totalCount = (filters.status === 'ACTIVE' ? (filteredActiveUsersCount ?? totalActiveUsersCount) : undefined) ?? scannedCount ?? users.length

  // The export runs through the reports pipeline, whose worker enforces reportsAccess
  // (Admin-only by default; location admins count as Admin). Without this gate an
  // unauthorized approver would get a silent 16-minute failure instead of no button.
  const canExport = useMemo(() => {
    if (!authCompany) {
      return false
    }
    const exporterRole = amIAdmin || isLocationAdmin ? UserRoleEnum.Admin : permissions.isApprover ? UserRoleEnum.Approver : UserRoleEnum.User
    return resolveFeaturePermissions(authCompany).featureVisibility.reportsAccess.includes(exporterRole)
  }, [authCompany, amIAdmin, isLocationAdmin, permissions.isApprover])

  const viewTabs: { key: UsersPageViewTab; label: React.ReactNode }[] = [
    { key: 'profile', label: <IntlMessages id="users.table.tabProfile" /> },
    { key: 'leaveDetails', label: <IntlMessages id="users.table.tabLeaveDetails" /> },
  ]

  return (
    <>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <div className="main-content">
          <PageHeader
            title={<IntlMessages id="app.users" />}
            actions={
              <>
                <VotitoSurveyConnected surveyId={USERS_TABLE_SURVEY_ID} />
                {amIAdmin && !isLocationAdmin && platform !== 'email' && !shouldDisableFeatureFlagSyncUsers && <SyncUsersControls />}
                {amIAdmin && platform == 'email' && importEmailUsersEnabled && (
                  <Tooltip title={notInvitedUsers.length === 0 ? <IntlMessages id="users.table.inviteNoneAwaiting" /> : <IntlMessages id="users.inviteUsersDescriptionShort" />}>
                    <Button
                      aria-disabled={notInvitedUsers.length === 0}
                      className={notInvitedUsers.length === 0 ? 'vt-users-invite-button--empty' : undefined}
                      onClick={() => {
                        if (notInvitedUsers.length === 0) {
                          return
                        }
                        setInvites(notInvitedUsers)
                        setShowInviteUsersModal(true)
                      }}
                    >
                      <MailOutlined style={{ marginRight: 5 }} />
                      <IntlMessages id="users.inviteUsers" />
                      {notInvitedUsers.length > 0 && (
                        <span className="vt-users-invite-count">
                          <IntlMessages id="users.table.inviteAwaitingCount" values={{ count: notInvitedUsers.length }} />
                        </span>
                      )}
                    </Button>
                  </Tooltip>
                )}
                {amIAdmin && !isLocationAdmin && (
                  <Tooltip title={platform === 'email' && importEmailUsersEnabled ? <IntlMessages id="users.createUsersDescriptionShort" /> : null}>
                    <Button type="primary" onClick={importNewUsers} className={shouldHighlightAddUsers ? 'users-add-button-highlight' : undefined}>
                      <PlusCircleOutlined style={{ marginRight: 5 }} />
                      <IntlMessages id={getCTAButtonTranslation()} />
                    </Button>
                  </Tooltip>
                )}
              </>
            }
          />
          <div className="main-content-body">
            <div className="vt-users-view-tabs" role="tablist">
              {viewTabs.map((tab) => (
                <Button
                  key={tab.key}
                  role="tab"
                  aria-selected={viewTab === tab.key}
                  type={viewTab === tab.key ? 'primary' : 'default'}
                  ghost={viewTab === tab.key}
                  onClick={() => {
                    // Only a real switch: the active tab stays clickable, and re-clicking it
                    // would otherwise report a view change that never happened.
                    if (tab.key !== viewTab) {
                      track('USERS_VIEW_CHANGED', { view: tab.key, previousView: viewTab })
                    }
                    setViewTab(tab.key)
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
            <Row style={{ marginTop: 12 }} gutter={[8, 8]} align="middle">
              <Col>
                <Search key={searchResetKey} displaySpinner={true} onLoad={usersQuery.isFetchingNewList} onSearch={onSearch} placeholder={formatMessage({ id: 'users.search' })} />
              </Col>
              <Col flex="auto">
                <UsersFilterBar
                  filters={filters}
                  statusOptions={statusOptions}
                  locations={locationFilterOptions}
                  departments={departmentFilterOptions}
                  labels={labelFilterOptions}
                  labelsEnabled={shouldEnableFeatures}
                  isScopeLocked={isLocationAdmin || (permissions.isApprover && !isFullAdmin)}
                  hasActiveFilters={usersFilters.hasActiveDimensionFilters || inviteStatusFilter.length > 0}
                  onStatusChange={changeStatusFilter}
                  onDimensionChange={(dimension, values) => {
                    usersFilters.setDimension(dimension, values)
                  }}
                  onClearAll={() => {
                    setInviteStatusFilter([])
                    setSearchResetKey((key) => key + 1)
                    usersFilters.clearAll()
                  }}
                  {...(inviteFilterEnabled
                    ? {
                        inviteStatuses: inviteStatusFilter,
                        inviteStatusOptions,
                        onInviteStatusesChange: (values: EmailInviteStatusEnum[]) => {
                          setInviteStatusFilter(values)
                        },
                      }
                    : {})}
                />
              </Col>
              <Col>
                {viewTab === 'profile' ? (
                  <ColumnSettingsPopover
                    options={columnSettingsOptions}
                    columnOrder={columnPrefs.columnOrder}
                    columnVisibility={columnPrefs.columnVisibility}
                    setColumnOrder={columnPrefs.setColumnOrder}
                    setColumnHidden={columnPrefs.setColumnHidden}
                    showAll={columnPrefs.showAll}
                    resetToDefault={columnPrefs.resetToDefault}
                    leaveUnit={leaveDisplayUnit}
                    onLeaveUnitChange={setLeaveDisplayUnit}
                    density={{ value: density, onChange: setDensity }}
                    seeBalances={{ value: showBalance, onChange: setShowBalance }}
                  />
                ) : (
                  <ColumnSettingsPopover
                    options={leaveColumnSettingsOptions}
                    sortable={false}
                    columnOrder={leaveColumnPrefs.columnOrder}
                    columnVisibility={leaveColumnPrefs.columnVisibility}
                    setColumnOrder={leaveColumnPrefs.setColumnOrder}
                    setColumnHidden={leaveColumnPrefs.setColumnHidden}
                    showAll={leaveColumnPrefs.showAll}
                    resetToDefault={leaveColumnPrefs.resetToDefault}
                    leaveUnit={leaveDisplayUnit}
                    onLeaveUnitChange={setLeaveDisplayUnit}
                    density={{ value: density, onChange: setDensity }}
                    seeBalances={{ value: showBalance, onChange: setShowBalance }}
                  />
                )}
              </Col>
              {canExport && (
                <Col>
                  <Button icon={<DownloadOutlined />} disabled={users.length === 0} onClick={() => setShowExportModal(true)}>
                    <IntlMessages id="export.export" />
                  </Button>
                </Col>
              )}
            </Row>
            <UsersTableSection
              filters={effectiveFilters}
              reference={reference}
              columnPrefs={columnPrefs}
              view={viewTab}
              leaveDetails={{
                activeLeaveTypes,
                columnPrefs: leaveColumnPrefs,
                displayUnit: leaveDisplayUnit,
                activated: leaveTabActivated,
                showBalance,
              }}
              density={density}
              energyEnabled={energyEnabled}
              invites={{ getStatus: getUserInviteStatus, onInvite: confirmInviteUser }}
              fetchLocationDeleteSuggestion={fetchLocationDeleteSuggestion}
              onSuggestLocationDelete={suggestLocationDelete}
              onActiveCountDelta={adjustActiveCounts}
              totalCount={totalCount}
            />
            {showInviteUsersModal && (
              <Modal
                title={formatMessage({ id: 'users.inviteUsers' })}
                open={showInviteUsersModal}
                onOk={() => {
                  sendInvites()
                }}
                confirmLoading={isSubmittingInviteRequest}
                okButtonProps={{ disabled: invites.length === 0 }}
                onCancel={() => {
                  setShowInviteUsersModal(false)
                }}
                okText={formatMessage({ id: 'app.sendInvites' })}
              >
                <InviteAndManageUsersWithEmail
                  selectedLocale={invitationLocale}
                  removeInvite={removeInvite}
                  changeLocale={changeLocale}
                  authUserEmail={email}
                  invitedUsers={invites}
                  showInputField={!importEmailUsersEnabled}
                />
                {pendingInvites.length > 0 && (
                  <div className="vt-users-invite-pending">
                    <Typography.Text strong>
                      <IntlMessages id="users.table.pendingInvites" />
                    </Typography.Text>
                    {pendingInvites.map((invite) => (
                      <div key={invite.email} className="vt-users-invite-pending-row">
                        <span className="vt-users-invite-pending-email">{invite.email}</span>
                        <StatusBadge tone="warning">{formatMessage({ id: 'app.pending' })}</StatusBadge>
                        <Button type="link" size="small" loading={reinvitingEmail === invite.email} onClick={() => reinviteSingle(invite.email)}>
                          <IntlMessages id="users.reinvite" />
                        </Button>
                        <Button type="link" size="small" danger onClick={() => showConfirmDeleteInvite(invite.email)}>
                          <IntlMessages id="app.delete" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Modal>
            )}
            {confirmState && (
              <ConfirmModal
                open
                title={confirmState.title}
                message={confirmState.message}
                primaryAction={{
                  label: confirmState.okLabel,
                  variant: confirmState.danger ? 'primary-danger' : 'primary',
                  onClick: () => {
                    confirmState.onOk()
                    setConfirmState(null)
                  },
                }}
                secondaryAction={{ label: formatMessage({ id: 'app.cancel' }), onClick: () => setConfirmState(null) }}
                onClose={() => setConfirmState(null)}
              />
            )}
            {showExportModal && (
              <ExportUsersModal
                open
                onClose={() => setShowExportModal(false)}
                filters={filters}
                inviteStatuses={inviteStatusFilter}
                inviteStatusOptions={inviteFilterEnabled ? inviteStatusOptions : undefined}
                filterBarOptions={{
                  statusOptions,
                  locations: locationFilterOptions,
                  departments: departmentFilterOptions,
                  labels: labelFilterOptions,
                  labelsEnabled: shouldEnableFeatures,
                  isScopeLocked: isLocationAdmin || (permissions.isApprover && !isFullAdmin),
                }}
                displayUnit={leaveDisplayUnit}
                filteredTotal={totalCount}
                columns={exportColumns}
                columnsReady={leaveTypesLoaded}
                authUser={{ id: authUserId, platform }}
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default UsersPage
